import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuthenticator } from '@aws-amplify/ui-react';

import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Avatar,
  Card,
  CardContent,
  Divider,
  IconButton,
  TextField,
  MenuItem,
  Skeleton,
  CircularProgress,
  Badge,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  SwipeableDrawer,
  Collapse,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Person as PersonIcon,
  BarChart as BarChartIcon,
  Insights as InsightsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  AccountCircle as ProfileIcon,
  Assignment as TasksIcon,
  Lightbulb as SuggestionsIcon,
  Analytics as StatsIcon
} from '@mui/icons-material';

import { useGetUserOutgoingReqQuery } from '../../redux/thunks/users/useroutgoingrequests';
import { useAddTrustedUserMutation } from '../../redux/thunks/users/useraddtrusteduser';
import { useAddTrustedUserResMutation } from '../../redux/thunks/users/useraddtrusteduserres';
import { useGetUserIncomingReqQuery } from '../../redux/thunks/users/userincomingrequests';
import { useGetNetworkStatsQuery } from '../../redux/thunks/users/getnetworkstats';
import { usePrefetch } from '../../redux/thunks/users/userprofile';
import NetworkMiddleColumn from './NetworkMiddleColumn';
import NetworkInsightsColumn from './NetworkInsightsColumn';

// All existing skeleton components remain exactly the same
const NetworkStatsSkeleton = () => (
  <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
    <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
    <Grid container spacing={2}>
      {[1, 2, 3, 4].map((item) => (
        <Grid item xs={6} key={item}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="80%" height={24} />
        </Grid>
      ))}
    </Grid>
  </Paper>
);

const QuickActionsSkeleton = () => (
  <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
    <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
    {[1, 2, 3].map((item) => (
      <Skeleton 
        key={item}
        variant="rectangular" 
        height={36} 
        sx={{ mb: 2, borderRadius: 1 }}
      />
    ))}
  </Paper>
);

const CategoriesSkeleton = () => (
  <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
    <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
    {[1, 2, 3, 4].map((item) => (
      <Box key={item} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="20%" />
      </Box>
    ))}
  </Paper>
);

const ConnectionCardSkeleton = () => (
  <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Skeleton variant="circular" width={50} height={50} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" sx={{ mb: 1 }} />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="40%" />
      </Box>
      <Box sx={{ width: 100 }}>
        <Skeleton variant="text" width="100%" />
      </Box>
    </Box>
  </Paper>
);

const NetworkInsightsSkeleton = () => (
  <>
    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
      <Skeleton variant="text" width="70%" height={32} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="90%" />
    </Paper>
    
    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
      <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={180} />
    </Paper>
    
    <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
      <Skeleton variant="text" width="65%" height={32} sx={{ mb: 2 }} />
      {[1, 2, 3, 4].map((item) => (
        <Box key={item} sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" height={60} />
        </Box>
      ))}
    </Paper>
  </>
);

// 🆕 Mobile Network Sidebar Component
const MobileNetworkSidebar = ({ 
  isOpen, 
  onClose,
  thisUser,
  incoming,
  outgoing,
  networkStatsData,
  navigate
}) => {
  const [expandedSections, setExpandedSections] = useState({
    stats: true,
    actions: false,
    insights: false,
    categories: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const CONNECTION_CATEGORIES = [
    { name: 'Follows', count: networkStatsData?.networkFollows || 0 },
    { name: 'Referrals', count: networkStatsData?.networkReferred || 0},
    { name: 'Reviews', count: networkStatsData?.networkReviews || 0},
    { name: 'Views', count: networkStatsData?.networkViews || 0}
  ];

  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          width: '100%',
          maxWidth: 400,
          bgcolor: 'background.default'
        }
      }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Network Menu
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
        {/* Network Stats Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('stats')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StatsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Network Overview</Typography>
            </Box>
            {expandedSections.stats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.stats}>
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h5" color="primary">
                      {thisUser.myCurrentNetworkList?.userDetails?.length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Extended Network
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h5" color="primary">
                      {incoming?.pendingInTrustedUsers?.length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requests Waiting
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h5" color="primary">
                      {(thisUser.trustScore || 50).toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Trust Score
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h5" color="primary">
                      {outgoing?.pendingOutTrustedUsers?.length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pending Requests
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Collapse>
        </Card>

        {/* Quick Actions Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('actions')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TasksIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Quick Actions</Typography>
            </Box>
            {expandedSections.actions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.actions}>
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    navigate('/usersearch');
                    onClose();
                  }}
                  size="large"
                >
                  Find Connections
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<GroupIcon />}
                  sx={{
                    position: 'relative',
                    justifyContent: 'flex-start',
                  }}
                  size="large"
                >
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: 8,
                      fontSize: '0.7rem',
                      color: 'orange',
                      fontWeight: 'bold',
                    }}
                  >
                    Coming Soon
                  </Typography>
                  Import Contacts
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={() => {
                    navigate(`/profile/${thisUser._id}/active`);
                    onClose();
                  }}
                  size="large"
                >
                  Manage Profile
                </Button>
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Network Categories Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('categories')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BarChartIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Network Stats</Typography>
            </Box>
            {expandedSections.categories ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.categories}>
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {CONNECTION_CATEGORIES.map((category) => (
                  <Box key={category.name} sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    p: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1
                  }}>
                    <Typography variant="body2">{category.name}</Typography>
                    <Chip 
                      label={category.count} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Network Insights Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('insights')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InsightsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Insights</Typography>
            </Box>
            {expandedSections.insights ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.insights}>
            <CardContent sx={{ pt: 0 }}>
              <NetworkInsightsColumn userData={thisUser} />
            </CardContent>
          </Collapse>
        </Card>
      </Box>
    </SwipeableDrawer>
  );
};

// 🆕 Mobile Network Stats Banner
const MobileNetworkBanner = ({ thisUser, incoming, outgoing, onOpenSidebar }) => {
  return (
    <Box 
      sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        p: 2,
        mb: 3 
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            My Network
          </Typography>
          <IconButton 
            color="inherit" 
            onClick={onOpenSidebar}
          >
            <MenuIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-around',
          alignItems: 'center',
          gap: 1
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="white" sx={{ fontWeight: 600 }}>
              {thisUser.myCurrentNetworkList?.userDetails?.length || 0}
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Connections
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="white" sx={{ fontWeight: 600 }}>
              {incoming?.pendingInTrustedUsers?.length || 0}
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Requests
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="white" sx={{ fontWeight: 600 }}>
              {(thisUser.trustScore || 50).toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Trust Score
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// All existing component functions remain exactly the same
const SentRequestCard = ({ request, onResend, onCancel }) => {
  const navigate = useNavigate();

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(new Date(request.creationTime));
  
  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 2, border: 'none', cursor: 'pointer' }} onClick={() => navigate(`/profile/${request.trustedUserId}/active`)}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Avatar src={request.profileImageUrl} sx={{ width: 50, height: 50 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">{request.displayName}</Typography>
          
          <Typography variant="body2" color="text.secondary">
            Sent on {formattedDate}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onResend(request);
            }}
            sx={{ minWidth: 'auto' }}
          >
            Resend
          </Button>
          <Button 
            variant="outlined" 
            color="error" 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onCancel(request);
            }}
            sx={{ minWidth: 'auto' }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
  
const ReceivedInviteCard = ({ invite, onAccept, onReject, loading }) => {
  const navigate = useNavigate();
  
  const formattedDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(new Date(invite.creationTime));
  
  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 2, border: 'none', cursor: 'pointer' }} onClick={() => navigate(`/profile/${invite.trustedUserId}/active`)}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Avatar src={invite.profileImageUrl} sx={{ width: 50, height: 50 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">{invite.displayName}</Typography>
         
          <Typography variant="body2" color="text.secondary">
            Received on {formattedDate}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onAccept(invite);
            }}
            sx={{ minWidth: 'auto' }}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Processing' : 'Accept'}
          </Button>
          <Button 
            variant="outlined" 
            color="error" 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onReject(invite);
            }}
            sx={{ minWidth: 'auto' }}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Decline
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const NetworkStatsCard = ({ user, incoming, outgoing }) => {
  return (
    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
      <Typography variant="h6" gutterBottom>Network Overview</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="h4" color="primary">{user.myCurrentNetworkList.userDetails.length}</Typography>
          <Typography variant="body2" color="text.secondary">Extended Network</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h4" color="primary">{incoming.pendingInTrustedUsers.length}</Typography>
          <Typography variant="body2" color="text.secondary">Requests Waiting</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h4" color="primary"> {(user.trustScore || 50).toFixed(1)}%</Typography>
          <Typography variant="body2" color="text.secondary">Trust Score</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h4" color="primary">{outgoing.pendingOutTrustedUsers.length}</Typography>
          <Typography variant="body2" color="text.secondary">Pending Requests</Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

const QuickActionsCard = ({user}) => {
  const navigate = useNavigate();

  return (
    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
      <Typography variant="h6" gutterBottom>
        Quick Actions
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/usersearch')}
        >
          Find Connections
        </Button>

        <Button
          variant="outlined"
          startIcon={<GroupIcon />}
          sx={{
            position: 'relative',
            justifyContent: 'flex-start',
          }}
        >
          <Typography
            sx={{
              position: 'absolute',
              top: -2,
              right: 8,
              fontSize: '0.7rem',
              color: 'orange',
              fontWeight: 'bold',
            }}
          >
            Coming Soon
          </Typography>
          Import Contacts
        </Button>

        <Button
          variant="outlined"
          startIcon={<PersonIcon />}
          onClick={() => navigate(`/profile/${user._id}/active`)}
        >
          Manage Profile
        </Button>
      </Box>
    </Paper>
  );
};

const CategoriesCard = ({ stats }) => {

  const CONNECTION_CATEGORIES = [
    { name: 'Follows', count: stats.networkFollows || 0 },
    { name: 'Referrals', count: stats.networkReferred || 0},
    { name: 'Reviews', count: stats.networkReviews || 0},
    { name: 'Views', count: stats.networkViews || 0}
  ];
  
  return (
  <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
    <Typography variant="h6" gutterBottom>Network Stats</Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {CONNECTION_CATEGORIES.map((category) => (
        <Box key={category.name} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">{category.name}</Typography>
          <Typography variant="body2" color="text.secondary">({category.count})</Typography>
        </Box>
      ))}
    </Box>
  </Paper>
)};

const ConnectionCard = ({ connection }) => {
  const navigate = useNavigate();

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(new Date(connection.creationTime));
    
  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 2, border: 'none', cursor: 'pointer' }} onClick={() => navigate(`/profile/${connection.userId}/active`)}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Avatar src={connection.profileImageUrl} sx={{ width: 50, height: 50 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">{connection.displayName}</Typography>
          <Typography variant="body2" color="text.secondary">

          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connected since {formattedDate}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="primary">
            Trust Score: {connection.trustRating}%
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

// 🆕 Enhanced Main Component with Mobile Support
export default function NetworkPage() {
  const navigate = useNavigate();
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile-specific state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userId = useSelector((state) => state.auth.userId)  
  const thisUser = useSelector((state) => state.auth?.user || '');
  const token = useSelector((state) => state.auth?.token || null);
  const firstDegrees = useSelector((state) => state.auth?.user?.firstDegreeFriends || []);

  const isAuthenticated = authStatus === 'authenticated' && token;

  const [loading, setLoading] = useState(true);
  const [showRequests, setShowRequests] = useState(false); 
  const [pendingInvites, setPendingInvites] = useState([]); 
  const [sentInvites, setSentInvites] = useState([]);

  const [addTrustedUserRes, { isLoading: addTrustedUserResLoading }] = useAddTrustedUserResMutation();
  const { data: incoming, isLoading: incomingLoading, isSuccess: incomingSuccess } = useGetUserIncomingReqQuery(userId, {
    skip: !isAuthenticated
  });
  const { data: outgoing, isLoading: outgoingLoading, isSuccess: outgoingSuccess } = useGetUserOutgoingReqQuery(userId, {
    skip: !isAuthenticated
  });
  const [addTrustedUser, { isLoading: addTrustedUserLoading }] = useAddTrustedUserMutation();
  const { data: networkStatsData,  isError: isNetworkStatsDataError,  isLoading: isNetworkStatsDataLoading,  error: networkStatsDataError,} = useGetNetworkStatsQuery({
    skip: !isAuthenticated
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth/signin', { state: {name: 'network', link: `/network`}});
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {   
    setLoading(incomingLoading || outgoingLoading || isNetworkStatsDataLoading); 
  }, [incomingLoading, outgoingLoading, isNetworkStatsDataLoading]);

  useEffect(() => {
    if (incomingSuccess) {
      const pendingInvitesData = incoming.pendingInTrustedUsers.filter((invite) => invite.state === 'notification');   
      setPendingInvites(pendingInvitesData);
    }
  }, [incoming, incomingSuccess]);

  useEffect(() => {
    if (outgoingSuccess) {
      const pendingOutvites = outgoing.pendingOutTrustedUsers;   
      setSentInvites(pendingOutvites);
    }
  }, [outgoing, outgoingSuccess]);

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // All handler functions remain exactly the same
  const handleResendRequest = async (request) => {
    const payload = { 'trustedUserId': request.trustedUserId, requestType: 'reminder' };
    await addTrustedUser({payload, userId: request.trustedUserId});
  };

  const handleCancelRequest = async (request) => {
    const payload = { 'trustedUserId': request.trustedUserId, requestType: 'withdraw' };
    await addTrustedUser({payload, userId: request.trustedUserId});
    
    setSentInvites((prevInvites) => prevInvites.filter((prevInvite) => 
      prevInvite.trustedUserId !== request.trustedUserId
    ));
  };

  const handleAcceptInvite = async (invite) => {
    const payload = { 'trustedUserId': invite.trustedUserId, response: 1, 'userId': userId };
    await addTrustedUserRes(payload);
    
    setPendingInvites((prevInvites) => prevInvites.filter((prevInvite) => 
      prevInvite.trustedUserId !== invite.trustedUserId
    ));
  };

  const handleRejectInvite = async (invite) => {
    const payload = { 'trustedUserId': invite.trustedUserId, response: 0, 'userId': userId };
    await addTrustedUserRes(payload);
    
    setPendingInvites((prevInvites) => prevInvites.filter((prevInvite) => 
      prevInvite.trustedUserId !== invite.trustedUserId
    ));
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ bgcolor: 'primary.main', p: 3, mb: 3 }}>
          <Container maxWidth="xl">
            <Skeleton variant="text" width="300px" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          </Container>
        </Box>

        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <NetworkStatsSkeleton />
              <QuickActionsSkeleton />
              <CategoriesSkeleton />
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
                </Box>
              </Paper>
              {[1, 2, 3].map((item) => (
                <ConnectionCardSkeleton key={item} />
              ))}
            </Grid>

            <Grid item xs={12} md={3}>
              <NetworkInsightsSkeleton />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      {/* Network Banner - Responsive */}
      {isMobile ? (
        <MobileNetworkBanner 
          thisUser={thisUser} 
          incoming={incoming} 
          outgoing={outgoing}
          onOpenSidebar={() => setSidebarOpen(true)}
        />
      ) : (
        <Box 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            p: 3,
            mb: 3 
          }}
        >
          <Container maxWidth="xl">
            <Typography variant="h4">My Network</Typography>
          </Container>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        {isMobile ? (
          // Mobile Layout - Single Column
          <>
            {/* Mobile Content Header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 3
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {showRequests ? 'Connection Requests' : 'My Connections'}
              </Typography>
              <Button
                variant={showRequests ? "contained" : "outlined"}
                onClick={() => setShowRequests(!showRequests)}
                size="small"
                startIcon={
                  <Badge 
                    badgeContent={pendingInvites.length + sentInvites.length} 
                    color="error"
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}
                  >
                    <HourglassEmptyIcon />
                  </Badge>
                }
              >
                {showRequests ? 'Requests' : 'Requests'}
              </Button>
            </Box>

            {/* Mobile Content - Full Width */}
            {showRequests ? (
              <>
                {/* Sent Requests Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
                    Sent Connection Requests
                  </Typography>
                  {sentInvites.length > 0 ? (
                    sentInvites.map((request) => (
                      <SentRequestCard
                        key={request.trustedUserId}
                        request={request}
                        onResend={handleResendRequest}
                        onCancel={handleCancelRequest}
                      />
                    ))
                  ) : (
                    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none', textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No pending sent requests
                      </Typography>
                    </Paper>
                  )}
                </Box>

                {/* Received Invites Section */}
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
                    Received Invites
                  </Typography>
                  {pendingInvites.length > 0 ? (
                    pendingInvites.map((invite) => (
                      <ReceivedInviteCard
                        key={invite.trustedUserId}
                        invite={invite}
                        onAccept={handleAcceptInvite}
                        onReject={handleRejectInvite}
                        loading={addTrustedUserResLoading}
                      />
                    ))
                  ) : (
                    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none', textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No pending received invites
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </>
            ) : (
              <NetworkMiddleColumn
                connections={firstDegrees || []}
                onFindConnections={() => navigate('/usersearch')}
                networkStats={networkStatsData}
                ConnectionCard={ConnectionCard}
              />
            )}

            {/* Mobile Network Sidebar */}
            <MobileNetworkSidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              thisUser={thisUser}
              incoming={incoming}
              outgoing={outgoing}
              networkStatsData={networkStatsData}
              navigate={navigate}
            />
          </>
        ) : (
          // Desktop Layout - Original 3 Columns (unchanged)
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={3}>
              <NetworkStatsCard user={thisUser} incoming={incoming} outgoing={outgoing} />
              <QuickActionsCard user={thisUser} />
              <CategoriesCard stats={networkStatsData} />
            </Grid>

            {/* Middle Column */}
            <Grid item xs={12} md={6}>
              <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    select
                    size="small"
                    defaultValue="all"
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="all">All Connections</MenuItem>
                    <MenuItem value="recent">Recently Added</MenuItem>
                    <MenuItem value="business">Business Partners</MenuItem>
                  </TextField>
                  <TextField
                    select
                    size="small"
                    defaultValue="recent"
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="recent">Recently Active</MenuItem>
                    <MenuItem value="trust">Trust Score</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                  </TextField>
                  <Button
                    variant="outlined"
                    onClick={() => setShowRequests(!showRequests)}
                    sx={{ ml: 'auto' }}
                  >
                    {showRequests ? 'Show Connections' : 'Show Requests'}
                  </Button>
                </Box>
              </Paper>

              {showRequests ? (
                <>
                  {/* Sent Requests Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
                      Sent Connection Requests
                    </Typography>
                    {sentInvites.length > 0 ? (
                      sentInvites.map((request) => (
                        <SentRequestCard
                          key={request.trustedUserId}
                          request={request}
                          onResend={handleResendRequest}
                          onCancel={handleCancelRequest}
                        />
                      ))
                    ) : (
                      <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none', textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No pending sent requests
                        </Typography>
                      </Paper>
                    )}
                  </Box>

                  {/* Received Invites Section */}
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
                      Received Invites
                    </Typography>
                    {pendingInvites.length > 0 ? (
                      pendingInvites.map((invite) => (
                        <ReceivedInviteCard
                          key={invite.trustedUserId}
                          invite={invite}
                          onAccept={handleAcceptInvite}
                          onReject={handleRejectInvite}
                          loading={addTrustedUserResLoading}
                        />
                      ))
                    ) : (
                      <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none', textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No pending received invites
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </>
              ) : (
                <NetworkMiddleColumn
                  connections={firstDegrees || []}
                  onFindConnections={() => navigate('/usersearch')}
                  networkStats={networkStatsData}
                  ConnectionCard={ConnectionCard}
                />
              )}
            </Grid>

            {/* Right Column - Network Insights */}
            <Grid item xs={12} md={3}>
              <NetworkInsightsColumn userData={thisUser} />
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
}